/** Phase 2A — Campaigns list + create form + detail w/ action bar.
 *  Phase 2B — view toggle (list/kanban), asset picker on create wizard.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowLeft, LayoutGrid, List as ListIcon } from "lucide-react";
import CampaignsKanban from "./CampaignsKanban";
import AssetPicker from "./AssetPicker";
import { TemplatePickerDialog } from "./CampaignsFromTemplate";
import KnowledgeGraphDrawer from "../../components/app/KnowledgeGraphDrawer";
import PublishConfirmDialog from "../../components/app/PublishConfirmDialog";

const TYPES = ["lead","reach","recruiting","retargeting","branding","webinar","appointment_booking","organic_content","launch"];
const STATUSES = ["draft","in_review","approved","rejected","scheduled","active","paused","completed","archived"];
const PLATFORMS = ["meta","google_search","google_display","youtube","linkedin","tiktok","instagram","facebook","email","whatsapp","organic_social"];
const KPIS = ["leads","reach","ctr","cpl","roas","applications","registrations","appointments","orders","awareness"];

const STATUS_COLORS = {
  draft:"secondary", in_review:"outline", approved:"default", rejected:"destructive",
  scheduled:"default", active:"default", paused:"secondary",
  completed:"secondary", archived:"outline",
};

function StatusBadge({status}){ return <Badge data-testid={`status-${status}`} variant={STATUS_COLORS[status]||"outline"}>{status}</Badge>; }

export function CampaignsList(){
  const [items,setItems]=useState([]);
  const [q,setQ]=useState("");
  const [status,setStatus]=useState("all");
  const [type,setType]=useState("all");
  const [view,setView]=useState(()=>localStorage.getItem("viona.campaigns.view")||"list");
  const [loading,setLoading]=useState(true);
  const [tplOpen,setTplOpen]=useState(false);
  const load=async()=>{
    setLoading(true);
    const params={};
    if(q) params.q=q;
    if(status!=="all"&&view==="list") params.status=status;
    if(type!=="all") params.campaign_type=type;
    if(view==="kanban") params.limit=200;
    try{const r=await api.get("/campaigns",{params}); setItems(r.data.items||[]);}finally{setLoading(false);}
  };
  useEffect(()=>{load();/* eslint-disable-next-line */},[q,status,type,view]);
  const setViewPersist=(v)=>{setView(v); localStorage.setItem("viona.campaigns.view",v);};
  return (
    <div data-testid="campaigns-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Campaigns</h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-card overflow-hidden">
            <button
              type="button"
              data-testid="view-list"
              onClick={()=>setViewPersist("list")}
              className={["px-3 py-1.5 text-sm flex items-center gap-1",
                          view==="list"?"bg-accent text-accent-foreground":"text-muted-foreground hover:bg-accent/40"].join(" ")}
            ><ListIcon className="size-4"/>List</button>
            <button
              type="button"
              data-testid="view-kanban"
              onClick={()=>setViewPersist("kanban")}
              className={["px-3 py-1.5 text-sm flex items-center gap-1 border-l",
                          view==="kanban"?"bg-accent text-accent-foreground":"text-muted-foreground hover:bg-accent/40"].join(" ")}
            ><LayoutGrid className="size-4"/>Kanban</button>
          </div>
          <Button variant="outline"
                  data-testid="from-template-btn"
                  onClick={()=>setTplOpen(true)}
                  title="Aus Vorlage erstellen">
            <Plus className="size-4 mr-1"/>From Template
          </Button>
          <Button asChild data-testid="new-campaign-btn"><Link to="/app/campaigns/new"><Plus className="size-4 mr-1"/>New Campaign</Link></Button>
        </div>
      </div>
      <TemplatePickerDialog open={tplOpen} onOpenChange={setTplOpen} />
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input data-testid="campaigns-search" placeholder="Search by name…" value={q} onChange={(e)=>setQ(e.target.value)} />
            {view === "list" && (
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
                <SelectContent><SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type"/></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>
                {TYPES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={load}>Refresh</Button>
          </div>
          {view === "kanban" ? (
            <CampaignsKanban items={items} onChanged={load} locale={localStorage.getItem("viona.locale")||"en"} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
                <TableHead>Platforms</TableHead><TableHead>Budget</TableHead><TableHead>Updated</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">…</TableCell></TableRow>}
                {!loading && items.length===0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No campaigns yet.</TableCell></TableRow>}
                {items.map(c=>(
                  <TableRow key={c.id} data-testid="campaign-row" className="cursor-pointer hover:bg-accent/30" onClick={()=>window.location.assign(`/app/campaigns/${c.id}`)}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><Badge variant="outline">{c.campaign_type}</Badge></TableCell>
                    <TableCell><StatusBadge status={c.status}/></TableCell>
                    <TableCell className="text-xs">{(c.platforms||[]).join(", ")||"—"}</TableCell>
                    <TableCell className="text-xs">€{c.budget?.amount||0}/{c.budget?.type==="daily"?"d":"total"}</TableCell>
                    <TableCell className="text-xs">{c.updated_at?new Date(c.updated_at).toLocaleDateString():"—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const EMPTY_DRAFT={
  name:"", campaign_type:"lead", goal_summary:"", goal_kpi:"leads",
  region_ids:[], target_group_ids:[], product_ids:[], service_ids:[],
  asset_ids:[],
  budget:{amount:50,currency:"EUR",type:"daily",pacing:"standard"},
  schedule:{start_at:null,end_at:null,timezone:"Europe/Berlin"},
  platforms:["meta"],
  offer:{headline:"",value_prop:"",cta:""},
  tracking:{utm_source:"meta",utm_medium:"cpc",utm_campaign:"",conversion_event:""},
  crm_goal:{connector_key:"viona",pipeline_stage:"",lead_source_tag:""},
  notes:"", tags:[],
};

function MultiSelectChips({label,options,value,onChange,testid}){
  const toggle=(id)=>onChange(value.includes(id)?value.filter(x=>x!==id):[...value,id]);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map(o=>(
          <button type="button" key={o.id} onClick={()=>toggle(o.id)}
            className={`text-xs px-2 py-1 rounded-full border ${value.includes(o.id)?"bg-foreground text-background border-foreground":"bg-transparent text-muted-foreground hover:bg-accent"}`}>
            {o.label}
          </button>
        ))}
        {options.length===0 && <span className="text-xs text-muted-foreground">none</span>}
      </div>
    </div>
  );
}

export function CampaignCreate(){
  const nav=useNavigate();
  const [draft,setDraft]=useState(EMPTY_DRAFT);
  const [brand,setBrand]=useState(null);
  const [submitting,setSubmitting]=useState(false);
  const [errors,setErrors]=useState(null);
  useEffect(()=>{api.get("/brand-profile").then(r=>setBrand(r.data));},[]);
  const u=(k,v)=>setDraft(d=>({...d,[k]:v}));
  const un=(k,sub,v)=>setDraft(d=>({...d,[k]:{...d[k],[sub]:v}}));
  const submit=async(e)=>{
    e.preventDefault(); setSubmitting(true); setErrors(null);
    if(!draft.tracking.utm_campaign) draft.tracking.utm_campaign=draft.name.toLowerCase().replace(/\s+/g,"_").slice(0,40);
    try{
      const r=await api.post("/campaigns",draft);
      toast.success("Campaign created"); nav(`/app/campaigns/${r.data.id}`);
    }catch(err){
      const status=err?.response?.status;
      const d=err?.response?.data?.detail;
      if(status===403||(d&&d.error==="permission_denied")){
        // Centralised humanized toast handles user-facing copy; don't leak raw JSON inline.
        setErrors("");
      }else{
        setErrors(typeof d==="string"?d:JSON.stringify(d));
        toast.error("Validation failed");
      }
    }finally{setSubmitting(false);}
  };
  if(!brand) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const regions=(brand.regions||[]).map(r=>({id:r.id,label:r.name}));
  const personas=(brand.buyer_personas||[]).map(p=>({id:p.id,label:p.name}));
  const products=(brand.products||[]).map(p=>({id:p.id,label:p.name}));
  return (
    <div data-testid="campaign-create-page" className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm"><Link to="/app/campaigns"><ArrowLeft className="size-4 mr-1"/>Campaigns</Link></Button>
      </div>
      <h1 className="text-3xl font-semibold">New Campaign</h1>
      {errors && <Card><CardContent className="pt-6 text-sm text-destructive break-all">{errors}</CardContent></Card>}
      <form onSubmit={submit} className="space-y-6">
        <Card><CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="c-name" value={draft.name} required onChange={(e)=>u("name",e.target.value)}/></div>
              <div><Label>Type</Label>
                <Select value={draft.campaign_type} onValueChange={(v)=>u("campaign_type",v)}>
                  <SelectTrigger data-testid="c-type"><SelectValue/></SelectTrigger>
                  <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Goal KPI</Label>
                <Select value={draft.goal_kpi} onValueChange={(v)=>u("goal_kpi",v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{KPIS.map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Target value (optional)</Label><Input type="number" value={draft.goal_target_value||""} onChange={(e)=>u("goal_target_value",e.target.value?Number(e.target.value):null)}/></div>
            </div>
            <div><Label>Goal summary</Label><Textarea rows={2} value={draft.goal_summary} onChange={(e)=>u("goal_summary",e.target.value)}/></div>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Audience</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <MultiSelectChips testid="c-regions" label="Regions" options={regions} value={draft.region_ids} onChange={(v)=>u("region_ids",v)}/>
            <MultiSelectChips testid="c-personas" label="Target groups" options={personas} value={draft.target_group_ids} onChange={(v)=>u("target_group_ids",v)}/>
            <MultiSelectChips testid="c-products" label="Products" options={products} value={draft.product_ids} onChange={(v)=>u("product_ids",v)}/>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Budget & Schedule</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Amount (€)</Label><Input type="number" data-testid="c-budget" value={draft.budget.amount} onChange={(e)=>un("budget","amount",Number(e.target.value))}/></div>
            <div><Label>Type</Label>
              <Select value={draft.budget.type} onValueChange={(v)=>un("budget","type",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="lifetime">Lifetime</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Pacing</Label>
              <Select value={draft.budget.pacing} onValueChange={(v)=>un("budget","pacing",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="standard">Standard</SelectItem><SelectItem value="accelerated">Accelerated</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Start</Label><Input type="date" onChange={(e)=>un("schedule","start_at",e.target.value?`${e.target.value}T00:00:00Z`:null)}/></div>
            <div><Label>End</Label><Input type="date" onChange={(e)=>un("schedule","end_at",e.target.value?`${e.target.value}T23:59:59Z`:null)}/></div>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Platforms</CardTitle></CardHeader>
          <CardContent>
            <MultiSelectChips testid="c-platforms" label="Active platforms" options={PLATFORMS.map(p=>({id:p,label:p}))} value={draft.platforms} onChange={(v)=>u("platforms",v)}/>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Creatives & Assets</CardTitle></CardHeader>
          <CardContent>
            <AssetPickerSection ids={draft.asset_ids} onChange={(v)=>u("asset_ids",v)}/>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Offer</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Headline</Label><Input value={draft.offer.headline} onChange={(e)=>un("offer","headline",e.target.value)}/></div>
            <div><Label>CTA</Label><Input value={draft.offer.cta} onChange={(e)=>un("offer","cta",e.target.value)}/></div>
            <div className="md:col-span-2"><Label>Value proposition</Label><Textarea rows={2} value={draft.offer.value_prop} onChange={(e)=>un("offer","value_prop",e.target.value)}/></div>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Tracking</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>UTM source</Label><Input value={draft.tracking.utm_source} onChange={(e)=>un("tracking","utm_source",e.target.value)} required/></div>
            <div><Label>UTM medium</Label><Input value={draft.tracking.utm_medium} onChange={(e)=>un("tracking","utm_medium",e.target.value)} required/></div>
            <div><Label>UTM campaign</Label><Input value={draft.tracking.utm_campaign} onChange={(e)=>un("tracking","utm_campaign",e.target.value)} placeholder="auto from name"/></div>
            <div className="md:col-span-3"><Label>Conversion event</Label><Input value={draft.tracking.conversion_event} onChange={(e)=>un("tracking","conversion_event",e.target.value)} placeholder="e.g. lead_form_submit"/></div>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">CRM goal</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Connector</Label><Input value={draft.crm_goal.connector_key||""} onChange={(e)=>un("crm_goal","connector_key",e.target.value)}/></div>
            <div><Label>Pipeline stage</Label><Input value={draft.crm_goal.pipeline_stage||""} onChange={(e)=>un("crm_goal","pipeline_stage",e.target.value)}/></div>
            <div><Label>Lead source tag</Label><Input value={draft.crm_goal.lead_source_tag||""} onChange={(e)=>un("crm_goal","lead_source_tag",e.target.value)}/></div>
          </CardContent>
        </Card>
        <div className="flex justify-end gap-2">
          <Button asChild variant="ghost"><Link to="/app/campaigns">Abbrechen</Link></Button>
          <Button type="submit" disabled={submitting} data-testid="c-submit">{submitting?"Creating…":"Create campaign"}</Button>
        </div>
      </form>
    </div>
  );
}

function AssetPickerSection({ids, onChange}){
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {ids.length ? `${ids.length} asset${ids.length === 1 ? "" : "s"} attached` : "No assets attached yet."}
        </p>
        <Button type="button" variant="outline" size="sm"
                data-testid="c-asset-picker-open" onClick={() => setOpen(true)}>
          Pick assets
        </Button>
      </div>
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {ids.map((id) => (
            <Badge key={id} variant="outline" className="font-mono text-[10px]">
              {id.slice(0, 8)}
              <button type="button" className="ml-1 text-muted-foreground hover:text-foreground"
                      onClick={() => onChange(ids.filter((x) => x !== id))}>×</button>
            </Badge>
          ))}
        </div>
      )}
      <AssetPicker open={open} onOpenChange={setOpen} value={ids} onChange={onChange} />
    </div>
  );
}

export function CampaignDetail(){
  const {id} = useParams();
  const nav=useNavigate();
  const [c,setC]=useState(null);
  const [acting,setActing]=useState(false);
  const [confirmPub,setConfirmPub]=useState(false);
  const [kgOpen,setKgOpen]=useState(false);
  const [kgNodeId,setKgNodeId]=useState(null);
  const load=async()=>{const r=await api.get(`/campaigns/${id}`); setC(r.data);};
  useEffect(()=>{load();/* eslint-disable-next-line */},[id]);
  useEffect(()=>{
    // Phase 10.1 — resolve campaign_id → KG node id (may not exist
    // yet if backfill hasn't touched this campaign).
    (async()=>{
      try{
        const r=await api.get(`/kg/nodes?node_type=Campaign&subject_ref=${id}`);
        const first=(r.data?.items||[])[0];
        setKgNodeId(first?.id||null);
      }catch(_){/* silent */}
    })();
  },[id]);
  const act=async(suffix,extra={})=>{
    setActing(true);
    try{await api.post(`/campaigns/${id}/${suffix}`,extra); toast.success(suffix); await load();}
    catch(e){const d=e?.response?.data?.detail; toast.error(typeof d==="string"?d:JSON.stringify(d||"failed"));}
    finally{setActing(false);}
  };
  // Dedicated publish action for the confirm dialog — returns the
  // response body so the dialog can render platform ids + Meta deep
  // link, and re-throws so the dialog can transition to error state.
  const doPublish = async () => {
    setActing(true);
    try {
      const r = await api.post(`/campaigns/${id}/publish`);
      await load();
      return r;
    } finally {
      setActing(false);
    }
  };
  if(!c) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div data-testid="campaign-detail-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3 sticky top-0 bg-background/95 backdrop-blur z-10 py-3 border-b">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm"><Link to="/app/campaigns"><ArrowLeft className="size-4 mr-1"/>Campaigns</Link></Button>
          <div>
            <div className="font-semibold text-lg">{c.name}</div>
            <div className="flex items-center gap-2 text-xs">
              <StatusBadge status={c.status}/>
              <Badge variant="outline">approval: {c.approval_status}</Badge>
              <Badge variant="outline">v{c.version}</Badge>
              {/* Phase 20a — publish-mode badge */}
              {c.external_platform_id && (
                <Badge
                  variant="outline"
                  className={c.mocked_publish
                    ? "bg-slate-100 text-slate-700 border-slate-300"
                    : "bg-emerald-100 text-emerald-800 border-emerald-300"}
                  data-testid="campaign-publish-mode-badge"
                >
                  {c.mocked_publish ? "Mock" : "Live"}
                </Badge>
              )}
              {c.external_url && !c.mocked_publish && (
                <a href={c.external_url} target="_blank" rel="noreferrer"
                   className="text-blue-600 hover:underline inline-flex items-center gap-1"
                   data-testid="campaign-external-link">
                  View on Meta
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2" data-testid="action-bar">
          <Button variant="outline" size="sm"
                  onClick={()=>setKgOpen(true)}
                  data-testid="btn-view-kg">
            View knowledge graph
          </Button>
          {c.status==="draft" && <Button onClick={()=>act("submit-review")} disabled={acting} data-testid="btn-submit">Submit for review</Button>}
          {c.status==="in_review" && <Badge variant="outline">Awaiting approval — see Approvals queue</Badge>}
          {c.status==="approved" && <Button onClick={()=>setConfirmPub(true)} disabled={acting} data-testid="btn-publish">Publish</Button>}
          {c.status==="scheduled" && <Button onClick={()=>act("resume")} disabled={acting} data-testid="btn-activate">Activate</Button>}
          {c.status==="active" && <><Button variant="outline" onClick={()=>act("pause")} disabled={acting} data-testid="btn-pause">Pause</Button><Button onClick={()=>act("complete")} disabled={acting} data-testid="btn-complete">Complete</Button></>}
          {c.status==="paused" && <Button onClick={()=>act("resume")} disabled={acting} data-testid="btn-resume">Resume</Button>}
          {(c.status==="completed"||c.status==="rejected") && <Button onClick={()=>act("archive")} disabled={acting} data-testid="btn-archive">Archive</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tracking">Tracking & CRM</TabsTrigger>
          <TabsTrigger value="approvals">Approvals ({(c.approvals||[]).length})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-3 pt-4">
          <Card><CardHeader><CardTitle className="text-base">Audience & schedule</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><b>Type:</b> {c.campaign_type}</div>
              <div><b>Goal KPI:</b> {c.goal_kpi} {c.goal_target_value?`(target ${c.goal_target_value})`:""}</div>
              <div><b>Regions:</b> {c.region_ids.length}</div>
              <div><b>Personas:</b> {c.target_group_ids.length}</div>
              <div><b>Platforms:</b> {(c.platforms||[]).join(", ")}</div>
              <div><b>Budget:</b> €{c.budget?.amount}/{c.budget?.type==="daily"?"day":"total"} ({c.budget?.pacing})</div>
              <div><b>Start:</b> {c.schedule?.start_at?new Date(c.schedule.start_at).toLocaleDateString():"—"}</div>
              <div><b>End:</b> {c.schedule?.end_at?new Date(c.schedule.end_at).toLocaleDateString():"—"}</div>
              {c.goal_summary && <div className="md:col-span-2"><b>Goal:</b> {c.goal_summary}</div>}
              {c.offer?.headline && <div className="md:col-span-2"><b>Offer:</b> {c.offer.headline} — {c.offer.value_prop}</div>}
            </CardContent></Card>
        </TabsContent>
        <TabsContent value="tracking" className="pt-4">
          <Card><CardContent className="pt-6 text-sm space-y-1.5 font-mono">
            <div>utm_source = <b>{c.tracking?.utm_source}</b></div>
            <div>utm_medium = <b>{c.tracking?.utm_medium}</b></div>
            <div>utm_campaign = <b>{c.tracking?.utm_campaign}</b></div>
            <div>conversion_event = <b>{c.tracking?.conversion_event||"—"}</b></div>
            <div>crm.connector = {c.crm_goal?.connector_key||"—"}</div>
            <div>crm.pipeline_stage = {c.crm_goal?.pipeline_stage||"—"}</div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="approvals" className="pt-4 space-y-2">
          {(c.approvals||[]).length===0 && <p className="text-sm text-muted-foreground">No approval requests yet.</p>}
          {(c.approvals||[]).map(a=>(
            <Card key={a.id}><CardContent className="pt-6 flex items-center justify-between">
              <div><Badge variant="outline" className="mr-2">{a.state}</Badge><span className="text-xs font-mono">{a.kind}</span><div className="text-xs text-muted-foreground mt-1">{a.justification}</div></div>
              <Button asChild variant="ghost" size="sm"><Link to={`/app/approvals?id=${a.id}`}>Open</Link></Button>
            </CardContent></Card>
          ))}
        </TabsContent>
        <TabsContent value="history" className="pt-4">
          <CampaignHistory id={id}/>
        </TabsContent>
      </Tabs>

      <PublishConfirmDialog
        open={confirmPub}
        onOpenChange={setConfirmPub}
        campaignId={id}
        onConfirm={doPublish}
      />
      <KnowledgeGraphDrawer
        nodeId={kgNodeId}
        open={kgOpen}
        onClose={()=>setKgOpen(false)}
      />
    </div>
  );
}

function CampaignHistory({id}){
  const [items,setItems]=useState([]);
  useEffect(()=>{api.get(`/campaigns/${id}/history`).then(r=>setItems(r.data.items||[]));},[id]);
  if(items.length===0) return <p className="text-sm text-muted-foreground">No history yet.</p>;
  return <Table><TableHeader><TableRow><TableHead>v</TableHead><TableHead>Updated</TableHead><TableHead>Change</TableHead></TableRow></TableHeader>
    <TableBody>{items.map(h=>(
      <TableRow key={h.version}><TableCell className="font-mono">v{h.version}</TableCell>
      <TableCell className="text-xs">{h.updated_at?new Date(h.updated_at).toLocaleString():"—"}</TableCell>
      <TableCell className="text-xs">{h.change_summary||"—"}</TableCell></TableRow>
    ))}</TableBody></Table>;
}
